import { CheckSquareOutlined } from '@ant-design/icons';
import { Empty, Typography } from 'antd';
import dayjs from 'dayjs';
import { useMemo, type CSSProperties, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import {
  isLayoutBlockType,
  type FormSection,
  type RepeatableColumn,
  type WorkflowField,
  type WorkflowStep,
  type WorkflowTemplate,
} from '@/api/workflows';

import { evaluateConditions } from './evaluateConditions';
import type { FieldValue } from './FieldRenderer';
import { fileIcon } from './fileIcon';
import { formatFileSize } from './formatFileSize';
import { hiddenBorrowedFieldIds } from './hiddenReveals';
import { collectPdfDocs } from './pdfPreviewActions';
import {
  getInstanceCount,
  qualifiedKey,
  renderInstanceLabel,
  scopeValuesToInstance,
  sectionMinInstances,
} from './sectionInstances';
import type { UploadedFilesStore } from './uploadedFiles';

const { Title, Paragraph, Text } = Typography;

// Divider hairline under every section title. Slightly heavier than the
// per-row separator in the old design so the section boundary is felt as a
// distinct band across the document.
const SECTION_HAIRLINE = '1px solid #e5e7eb';
// Muted grey used for row labels + descriptive intro copy.
const LABEL_COLOR = '#64748b';
// Bold body text — the value under each row label.
const VALUE_COLOR = '#0f172a';

/**
 * Read-only recap step the advisor lands on before eSignature. Renders a flat
 * scroll-through document (no accordion) with:
 *   • Intro block — page title + regulatory reminder that fields lock at sign.
 *   • One block per FormSection across every prior DynamicForm step. Each
 *     block has an uppercase title + a right-aligned "EDIT" link that
 *     navigates back to the section's owning step. Body is a 3-column CSS
 *     grid of input answers (label above value); RepeatableRow fields span
 *     the full width as their own labelled card block.
 *   • Uploaded documents + Generated forms sections at the bottom, styled
 *     the same way as the DynamicForm sections above them.
 *
 * Layout blocks authored on the runtime step (Notice / Paragraph /
 * StaticText / BulletedList / Divider / Image) are display-only context —
 * NOT the advisor's answers — so they're stripped from the recap. Sections
 * that contain only layout blocks disappear from the review entirely.
 *
 * Values come from FormShell's live `values` map. Nothing is fetched;
 * nothing is persisted by this step.
 */
export function ApplicationReviewStep({
  template,
  step,
  values,
  language,
  uploadedFiles,
  onJumpToStep,
}: {
  template: WorkflowTemplate;
  step: WorkflowStep;
  values: Record<string, FieldValue>;
  language: 'en' | 'fr';
  /** Advisor-uploaded document store — powers the Uploaded documents recap
   *  block. Optional: absent when this step is used outside FormShell (a
   *  future standalone /review preview could omit it, in which case the
   *  block just doesn't render). */
  uploadedFiles?: UploadedFilesStore;
  /** Jump back to a specific step (clicked from the section EDIT link). */
  onJumpToStep: (stepId: string) => void;
}) {
  const { t } = useTranslation();
  const isFr = language === 'fr';

  // Only steps that come BEFORE this review step contribute content — a
  // review placed mid-flow recaps what's been filled so far only.
  const reviewIndex = template.steps.findIndex((s) => s.id === step.id);
  const priorSteps = template.steps.slice(
    0,
    reviewIndex >= 0 ? reviewIndex : template.steps.length,
  );
  const dynamicSteps = priorSteps.filter((s) => s.type === 'DynamicForm');
  const additionalDocSteps = priorSteps.filter((s) => s.type === 'AdditionalDocuments');
  // Generated forms come from PdfFileUpload steps; scoped to priorSteps so a
  // mid-flow review doesn't preview PDFs the advisor hasn't reached yet.
  // Memoized because renderFilename walks the step tree per row.
  const generatedForms = useMemo(() => {
    const priorTemplate: WorkflowTemplate = { ...template, steps: priorSteps };
    return collectPdfDocs(priorTemplate, language, values);
  }, [template, priorSteps, language, values]);
  const pdfGenerationStepId = priorSteps.find((s) => s.type === 'PdfGeneration')?.id ?? null;

  // Assemble one FlatSection per authored FormSection across every prior
  // DynamicForm step. Order preserves the workflow's authored order (steps
  // in order, sections within each step in order). Hidden sections (unmet
  // visibility conditions) are dropped entirely. Sections with zero items
  // after visibility filtering are also dropped so a section that's all
  // hidden fields doesn't produce a bare header.
  // Reveal targets whose owning option is unticked — collected across every
  // prior step so a value typed while an option was ticked doesn't linger on
  // the recap after the advisor unticked it.
  const hiddenBorrowed = useMemo(() => {
    const all = new Set<string>();
    for (const s of dynamicSteps) {
      for (const id of hiddenBorrowedFieldIds(s, values)) all.add(id);
    }
    return all;
  }, [dynamicSteps, values]);

  const dynamicSections = useMemo<FlatSection[]>(() => {
    const out: FlatSection[] = [];
    for (const s of dynamicSteps) {
      for (const section of s.sections) {
        if (
          (section.conditions?.length ?? 0) > 0 &&
          !evaluateConditions(section.conditions, section.conditionCombinator, values)
        ) {
          continue;
        }
        // A repeatable section holds one set of answers PER INSTANCE, under
        // `inst:<sectionId>:<n>:<fieldId>` keys. Recap each instance as its
        // own block so a second Accountholder's answers actually appear —
        // reading the bare field id showed the whole section blank.
        const instanceCount = section.repeatable
          ? getInstanceCount(values, section.id, sectionMinInstances(section))
          : 1;
        const baseTitle =
          (isFr ? section.titleFr : section.titleEn) || section.titleEn || section.titleFr;
        for (let i = 0; i < instanceCount; i++) {
          const items = collectSectionItems(section, values, isFr, hiddenBorrowed, i);
          if (items.length === 0) continue;
          // Section is "editable" when it contains at least one input field
          // the advisor filled in (i.e. not purely a display block). Sections
          // that are all Paragraph / BulletedList / Notice still show but
          // without an EDIT link — there's nothing to edit.
          const hasEditableField = items.some((i2) => i2.kind === 'input');
          out.push({
            key: `${s.id}:${section.id}:${i}`,
            // Instances are numbered so "Accountholder 1" / "Accountholder 2"
            // read as distinct people rather than a repeated heading.
            title: section.repeatable ? renderInstanceLabel(section, i, language) : baseTitle,
            stepId: hasEditableField ? s.id : null,
            items,
          });
        }
      }
    }
    return out;
  }, [dynamicSteps, values, isFr, hiddenBorrowed, language]);

  const hasUploaded =
    !!uploadedFiles &&
    additionalDocSteps.length > 0 &&
    (uploadedFiles.bucket.length > 0 ||
      additionalDocSteps.some((s) =>
        (s.additionalDocumentEntries ?? []).some(
          (e) => (uploadedFiles.entries[e.id] ?? []).length > 0,
        ),
      ));
  const hasGeneratedForms = generatedForms.length > 0;

  if (dynamicSections.length === 0 && !hasUploaded && !hasGeneratedForms) {
    return (
      <Empty
        image={<CheckSquareOutlined style={{ fontSize: 48, color: '#cbd5e1' }} />}
        description={
          <Text type="secondary">{t('formRenderer.applicationReview.noPriorForms')}</Text>
        }
      />
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 40 }}>
      {/* Intro — page title + regulatory reminder. Sits above every section
          so the advisor lands on it first regardless of scroll position. */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <Title level={3} style={{ margin: 0, color: VALUE_COLOR }}>
          {t('formRenderer.applicationReview.introTitle')}
        </Title>
        <Paragraph style={{ margin: 0, fontSize: 14, color: LABEL_COLOR, lineHeight: 1.6 }}>
          {t('formRenderer.applicationReview.introBody')}
        </Paragraph>
      </div>

      {dynamicSections.map((sec) => (
        <SectionBlock
          key={sec.key}
          title={sec.title}
          onEdit={sec.stepId ? () => onJumpToStep(sec.stepId!) : undefined}
          editLabel={t('formRenderer.applicationReview.edit')}
          editAria={t('formRenderer.applicationReview.editAria')}
        >
          {sec.items.map((it, idx) => renderItem(it, idx, isFr))}
        </SectionBlock>
      ))}

      {hasUploaded && uploadedFiles && (
        <SectionBlock
          title={t('formRenderer.applicationReview.uploadedDocumentsTitle')}
          onEdit={undefined}
          editLabel={t('formRenderer.applicationReview.edit')}
          editAria={t('formRenderer.applicationReview.editAria')}
        >
          <UploadedDocsGrid
            additionalDocSteps={additionalDocSteps}
            uploadedFiles={uploadedFiles}
            onJumpToStep={onJumpToStep}
          />
        </SectionBlock>
      )}

      {hasGeneratedForms && (
        <SectionBlock
          title={t('formRenderer.applicationReview.generatedFormsTitle')}
          onEdit={pdfGenerationStepId ? () => onJumpToStep(pdfGenerationStepId) : undefined}
          editLabel={t('formRenderer.applicationReview.edit')}
          editAria={t('formRenderer.applicationReview.editAria')}
        >
          <GeneratedFormsGrid docs={generatedForms} />
        </SectionBlock>
      )}
    </div>
  );
}

/**
 * Section block wrapper — uppercase title on the left, blue "EDIT" link on
 * the right, hairline below, then children rendered inside a 3-column CSS
 * Grid. Grid rows auto-size to content; items whose `kind === 'layout'`
 * declare `grid-column: 1 / -1` so authored blocks span the full width
 * inline with the answer rows.
 */
function SectionBlock({
  title,
  onEdit,
  editLabel,
  editAria,
  children,
}: {
  title: string;
  onEdit?: () => void;
  /** Visible text shown INSIDE the EDIT link (e.g. "Edit" / "Modifier"). */
  editLabel: string;
  /** Accessible description read by screen readers — more explicit than the
   *  visible label so the auditory context spells out what the button does. */
  editAria: string;
  children: ReactNode;
}) {
  return (
    <section>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          marginBottom: 4,
        }}
      >
        <span
          style={{
            fontSize: 14,
            fontWeight: 700,
            letterSpacing: 0.8,
            textTransform: 'uppercase',
            color: VALUE_COLOR,
          }}
        >
          {title}
        </span>
        {onEdit && (
          <button
            type="button"
            onClick={onEdit}
            aria-label={editAria}
            style={{
              // Reset browser button chrome — this reads as a text link.
              background: 'transparent',
              border: 'none',
              padding: '4px 6px',
              cursor: 'pointer',
              color: '#0179C1',
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: 0.8,
              textTransform: 'uppercase',
            }}
          >
            {editLabel}
          </button>
        )}
      </div>
      <div style={{ borderBottom: SECTION_HAIRLINE, marginBottom: 20 }} />
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
          columnGap: 32,
          rowGap: 24,
        }}
      >
        {children}
      </div>
    </section>
  );
}

// ─── Item model + collect + render ─────────────────────────────────────────

type FlatSection = {
  key: string;
  title: string;
  /** Owning step id for the section's EDIT link. Null when the section is
   *  purely layout blocks — nothing to edit, link is suppressed. */
  stepId: string | null;
  items: SectionItem[];
};

type SectionItem =
  | { kind: 'input'; field: WorkflowField; display: string | null }
  | {
      kind: 'repeatable';
      field: WorkflowField;
      /** One entry per authored row — each is a `{ columnId → cell value }`
       *  map. Null when the advisor hasn't added any rows. */
      rows: Array<Record<string, unknown>>;
    };

// Exported for tests — the instance/reveal handling below is easy to regress
// and fails silently (a blank recap section, not an error).
export function collectSectionItems(
  section: FormSection,
  values: Record<string, FieldValue>,
  isFr: boolean,
  hiddenBorrowed: Set<string>,
  instanceIdx: number,
): SectionItem[] {
  const out: SectionItem[] = [];
  // Values of a repeatable section live under instance-qualified keys.
  const keyOf = (fieldId: string): string =>
    section.repeatable ? qualifiedKey(section.id, instanceIdx, fieldId) : fieldId;
  // Conditions name their source by bare field id; inside a repeatable
  // section that must resolve to THIS instance's answer, so the recap
  // hides exactly what the advisor's own instance had hidden.
  const condValues = scopeValuesToInstance(section, values, instanceIdx);
  for (const row of section.rows) {
    for (const col of row.columns) {
      const f = col.field;
      if (!f) continue;
      // Borrowed by a CheckboxGroup option that isn't revealing it — the
      // advisor can't see the field, so a stale value must not surface here.
      // Mirrors the same filter in buildApplicationSnapshot.
      if (hiddenBorrowed.has(f.id)) continue;
      // Field-level visibility conditions filter the item out entirely.
      if (
        (f.conditions?.length ?? 0) > 0 &&
        !evaluateConditions(f.conditions, f.conditionCombinator, condValues)
      ) {
        continue;
      }
      // Layout blocks (Notice / Paragraph / StaticText / BulletedList /
      // Divider / Image) are display-only context authored on the runtime
      // step — not answers the advisor filled in. The review recap only
      // needs to surface what the advisor entered, so we skip them entirely
      // here. A section that ends up empty after this filter gets dropped
      // by the caller's `items.length === 0` guard.
      if (isLayoutBlockType(f.type)) continue;
      // RepeatableRow gets its own kind — the flat "join columns with —"
      // serialization looks awful in a single cell (see FUNDS section in
      // the RDSP demo). A dedicated renderer laying each row out as a
      // stacked label:value block reads properly.
      if (f.type === 'RepeatableRow') {
        const rowsRaw = values[keyOf(f.id)];
        const rows = Array.isArray(rowsRaw) ? (rowsRaw as Array<Record<string, unknown>>) : [];
        out.push({ kind: 'repeatable', field: f, rows });
        continue;
      }
      const display = formatValue(f, values[keyOf(f.id)], isFr);
      out.push({ kind: 'input', field: f, display });
    }
  }
  return out;
}

function renderItem(item: SectionItem, idx: number, isFr: boolean): ReactNode {
  if (item.kind === 'input') {
    return <InputRow key={idx} field={item.field} display={item.display} isFr={isFr} />;
  }
  return <RepeatableBlock key={idx} field={item.field} rows={item.rows} isFr={isFr} />;
}

/**
 * Label-above-value input row — occupies a single 3-col grid cell. Label
 * is grey light 13 px; value is bold 14 px dark. Empty required inputs
 * render "Not provided" in italic muted grey so the reviewer notices what
 * wasn't answered.
 */
function InputRow({
  field,
  display,
  isFr,
}: {
  field: WorkflowField;
  display: string | null;
  isFr: boolean;
}) {
  const { t } = useTranslation();
  const rawLabel = (isFr ? field.labelFr : field.labelEn) || field.labelEn || field.labelFr || '';
  // Labels are authored with the ** markdown bold pair. Strip them for the
  // review recap (bold is the row's default style anyway).
  const label = rawLabel.replace(/\*\*/g, '').trim();
  const isEmpty = display === null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
      <Text style={{ fontSize: 13, color: LABEL_COLOR }}>{label}:</Text>
      <Text
        style={{
          fontSize: 14,
          fontWeight: 600,
          color: isEmpty ? '#6b7280' : VALUE_COLOR,
          fontStyle: isEmpty ? 'italic' : undefined,
          whiteSpace: 'pre-line',
          wordBreak: 'break-word',
        }}
      >
        {display ?? t('formRenderer.applicationReview.noValue')}
      </Text>
    </div>
  );
}

/**
 * Full-width layout block — spans all 3 columns of the grid so authored
 * Paragraph / BulletedList / StaticText / Notice / Divider / Image content
 * reads inline with the answer rows. Rendering is deliberately minimal
 * (no markdown, no rich-text runner) — the review is a read-only recap;
 * exact styling belongs in the runtime step. If the block has no
 * meaningful content (e.g. an empty StaticText), we render nothing so the
 * grid doesn't leave a phantom row.
 */
/**
 * Full-width RepeatableRow recap — spans all 3 columns of the parent grid.
 * Renders the field's label as a small caption, then one card per authored
 * row with its cells laid out in a 3-column mini-grid (label above value,
 * same treatment as the outer InputRow). Multi-row RepeatableRows stack
 * their cards with a subtle hairline separator between them. Zero-row /
 * unset RepeatableRows fall back to a single "Not provided" line so the
 * reviewer knows something was skipped rather than the block silently
 * vanishing.
 */
function RepeatableBlock({
  field,
  rows,
  isFr,
}: {
  field: WorkflowField;
  rows: Array<Record<string, unknown>>;
  isFr: boolean;
}) {
  const { t } = useTranslation();
  const style: CSSProperties = { gridColumn: '1 / -1', minWidth: 0 };
  const rawLabel = (isFr ? field.labelFr : field.labelEn) || field.labelEn || '';
  const label = rawLabel.replace(/\*\*/g, '').trim();
  const template: RepeatableColumn[] = field.repeatableTemplate ?? [];
  // Walk each row's cells against the authored column template so the
  // display order and labels match what the originator wired — the `values`
  // map keys on column.id, not label. Empty cells are hidden per row so a
  // half-filled row doesn't waste a grid slot on "—".
  const cards = rows
    .map((row) => {
      if (!row || typeof row !== 'object') return null;
      const cells = template
        .map((col) => {
          const raw = row[col.id];
          if (raw === null || raw === undefined || raw === '') return null;
          const colLabel = (isFr && col.labelFr ? col.labelFr : col.labelEn) || col.labelEn;
          return { label: colLabel.replace(/\*\*/g, '').trim(), value: String(raw) };
        })
        .filter((c): c is { label: string; value: string } => c !== null);
      return cells.length > 0 ? cells : null;
    })
    .filter((c): c is Array<{ label: string; value: string }> => c !== null);
  return (
    <div style={{ ...style, display: 'flex', flexDirection: 'column', gap: 12 }}>
      {label && <Text style={{ fontSize: 13, color: LABEL_COLOR }}>{label}:</Text>}
      {cards.length === 0 ? (
        <Text
          style={{
            fontSize: 14,
            color: '#6b7280',
            fontStyle: 'italic',
          }}
        >
          {t('formRenderer.applicationReview.noValue')}
        </Text>
      ) : (
        cards.map((cells, idx) => (
          <div
            key={idx}
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
              columnGap: 32,
              rowGap: 16,
              // Hairline between cards; the first card has no top border so it
              // sits flush with the caption.
              borderTop: idx > 0 ? SECTION_HAIRLINE : 'none',
              paddingTop: idx > 0 ? 16 : 0,
            }}
          >
            {cells.map((c, i) => (
              <div
                key={i}
                style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}
              >
                <Text style={{ fontSize: 13, color: LABEL_COLOR }}>{c.label}:</Text>
                <Text
                  style={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: VALUE_COLOR,
                    wordBreak: 'break-word',
                  }}
                >
                  {c.value}
                </Text>
              </div>
            ))}
          </div>
        ))
      )}
    </div>
  );
}

// ─── Uploaded documents + Generated forms grids ────────────────────────────
//
// Both share the same 3-col grid the DynamicForm sections use — one card per
// file / generated form.

function UploadedDocsGrid({
  additionalDocSteps,
  uploadedFiles,
  onJumpToStep,
}: {
  additionalDocSteps: WorkflowStep[];
  uploadedFiles: UploadedFilesStore;
  onJumpToStep: (stepId: string) => void;
}) {
  const { t } = useTranslation();
  type Row = {
    key: string;
    label: string;
    fileName: string;
    size: number;
    description: string | null;
    stepId: string;
  };
  const rows: Row[] = [];
  for (const s of additionalDocSteps) {
    for (const entry of s.additionalDocumentEntries ?? []) {
      const files = uploadedFiles.entries[entry.id] ?? [];
      for (const f of files) {
        rows.push({
          key: `${entry.id}:${f.uid}`,
          label: entry.label,
          fileName: f.file.name,
          size: f.file.size,
          description: null,
          stepId: s.id,
        });
      }
    }
  }
  const lastDocStepId = additionalDocSteps[additionalDocSteps.length - 1]?.id;
  for (const f of uploadedFiles.bucket) {
    rows.push({
      key: `bucket:${f.uid}`,
      label: t('formRenderer.applicationReview.optionalDocumentLabel'),
      fileName: f.file.name,
      size: f.file.size,
      description: f.description?.trim() ? f.description.trim() : null,
      stepId: lastDocStepId ?? '',
    });
  }
  if (rows.length === 0) {
    return (
      <Text type="secondary" style={{ fontSize: 13, gridColumn: '1 / -1' }}>
        {t('formRenderer.applicationReview.noDocuments')}
      </Text>
    );
  }
  return (
    <>
      {rows.map((r) => (
        <FileCard
          key={r.key}
          icon={fileIcon(r.fileName)}
          label={r.label}
          primary={r.fileName}
          secondary={`${formatFileSize(r.size)}${r.description ? ` · ${r.description}` : ''}`}
          onClick={r.stepId ? () => onJumpToStep(r.stepId) : undefined}
        />
      ))}
    </>
  );
}

function GeneratedFormsGrid({ docs }: { docs: ReturnType<typeof collectPdfDocs> }) {
  return (
    <>
      {docs.map((d) => (
        <FileCard
          key={d.entryId}
          icon={fileIcon(d.fileName)}
          label={d.label}
          primary={d.fileName}
          secondary=""
        />
      ))}
    </>
  );
}

/** One card in the Uploaded / Generated grid — icon + label above a filename
 *  + secondary meta. Whole card is clickable when a jump target exists so
 *  keyboard + touch users can reach the same edit affordance the section
 *  header's EDIT link provides. */
function FileCard({
  icon,
  label,
  primary,
  secondary,
  onClick,
}: {
  icon: ReactNode;
  label: string;
  primary: string;
  secondary: string;
  onClick?: () => void;
}) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        minWidth: 0,
        cursor: onClick ? 'pointer' : 'default',
      }}
      onClick={onClick}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
        {icon}
        <Text style={{ fontSize: 13, color: LABEL_COLOR }}>{label}</Text>
      </div>
      <Text
        style={{
          fontSize: 14,
          fontWeight: 600,
          color: VALUE_COLOR,
          wordBreak: 'break-word',
        }}
      >
        {primary}
      </Text>
      {secondary && (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {secondary}
        </Text>
      )}
    </div>
  );
}

// ─── formatValue — unchanged from earlier revs; workhorse for every input row ───

/**
 * Stringify a field's stored value for read-only display. Returns null when
 * the field is genuinely empty (so the row can render "Not provided" in
 * italics + grey). Resolves SingleSelect / Radio / Checkbox option values
 * back to their display labels in the advisor's language. Number values
 * pick up `$` / `%` prefixes per the field's numberFormat; Date values
 * format in DD/MM/YYYY so the recap matches what the advisor typed.
 */
function formatValue(field: WorkflowField, value: FieldValue, isFr: boolean): string | null {
  if (value === null || value === undefined) return null;

  // Date fields are stored as ISO strings (`YYYY-MM-DD`). Localise to the
  // same DD/MM/YYYY format the picker uses so the recap doesn't expose
  // the wire shape.
  if (field.type === 'Date' && typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.length === 0) return null;
    const d = dayjs(trimmed);
    return d.isValid() ? d.format('DD/MM/YYYY') : trimmed;
  }

  // Number fields — sample-values fixtures sometimes ship the value as a
  // string (`'125000'`); FormShell's onChange may also pass strings via
  // typed input. Parse-and-format here so a stringified number still gets
  // currency / percentage decoration.
  if (field.type === 'Number') {
    const n =
      typeof value === 'number'
        ? value
        : typeof value === 'string' && value.trim().length > 0
          ? Number(value)
          : NaN;
    if (!Number.isFinite(n)) return null;
    const formatted =
      field.numberFormat === 'currency'
        ? n.toLocaleString(isFr ? 'fr-CA' : 'en-CA', {
            style: 'currency',
            currency: 'CAD',
            // CAD options always include cents — strip them when the value is
            // a clean integer so $125,000 doesn't read as $125,000.00.
            minimumFractionDigits: Number.isInteger(n) ? 0 : 2,
          })
        : field.numberFormat === 'percentage'
          ? `${n.toLocaleString(isFr ? 'fr-CA' : 'en-CA')}%`
          : n.toLocaleString(isFr ? 'fr-CA' : 'en-CA');
    return formatted;
  }

  // Option-label lookup wins over the raw-string / boolean branches: a
  // SingleSelect / RadioGroup that happens to persist a stringified or
  // boolean value (e.g. 'true' / true for a Yes/No-shaped picker) still
  // resolves to the originator-authored option label in the advisor's
  // language. Falls through to the type-specific branches below when the
  // value doesn't match any option.
  if (field.type === 'SingleSelect' || field.type === 'RadioGroup') {
    const opt = field.options.find((o) => String(o.value) === String(value));
    if (opt) return (isFr ? opt.labelFr : opt.labelEn) || opt.labelEn || opt.labelFr;
  }
  if (typeof value === 'string') {
    if (value.trim().length === 0) return null;
    return value;
  }
  if (typeof value === 'number') return Number.isFinite(value) ? value.toString() : null;
  if (typeof value === 'boolean') {
    // A required Toggle/Checkbox that's still `false` is the acknowledgment
    // pattern — surface as "not provided" (null → italic empty-state) so
    // the advisor isn't misled into thinking they ticked it. Optional
    // boolean stays as a visible ✓ / — pair.
    if (field.required && value === false) return null;
    return value ? '✓' : '—';
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return null;
    if (field.type === 'CheckboxGroup') {
      const labels = value
        .map((v) => field.options.find((o) => o.value === v))
        .filter((o): o is NonNullable<typeof o> => !!o)
        .map((o) => (isFr ? o.labelFr : o.labelEn) || o.labelEn || o.labelFr)
        // Long regulatory clause labels frequently end with their own
        // trailing comma (e.g. "(a) … financial institution,"). Strip
        // the trailing punctuation so the join separator doesn't double
        // up to ",, ".
        .map((label) => label.replace(/[,;.\s]+$/, ''));
      return labels.join('; ');
    }
    // RepeatableRow value — array of objects keyed by column id. Resolve
    // each row to a "col-label: value" pair, join rows with newlines so
    // multi-row entries are readable in the recap.
    if (field.type === 'RepeatableRow' && field.repeatableTemplate) {
      const tmpl = field.repeatableTemplate;
      const rows = value as Array<Record<string, unknown>>;
      const formattedRows = rows
        .map((row) => {
          if (row === null || typeof row !== 'object') return '';
          const cells = tmpl
            .map((col) => {
              const cell = row[col.id];
              if (cell === null || cell === undefined || cell === '') return null;
              const colLabel = (isFr && col.labelFr ? col.labelFr : col.labelEn) || col.labelEn;
              return `${colLabel}: ${String(cell)}`;
            })
            .filter((s): s is string => s !== null);
          return cells.join(' — ');
        })
        .filter((s) => s.length > 0);
      if (formattedRows.length === 0) return null;
      return formattedRows.join('\n');
    }
    return value.map((v) => String(v)).join(', ');
  }
  return null;
}
